import { getLogger } from '@logtape/logtape';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/spotlight/styles.css';
import * as Sentry from '@sentry/react';
import { Amplify } from 'aws-amplify';
import { dequal } from 'dequal';
import { NuqsAdapter } from 'nuqs/adapters/react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { SWRConfig } from 'swr';

// Keep above ./app.tsx: `Sentry.init` must run before the app module graph is evaluated, or the router
// tracing wrapper degrades to the unwrapped `createBrowserRouter` and navigation spans are never emitted.
import './sentry.ts';

// Must stay above ./app.tsx: a module-scope logger created while the app graph is evaluated would
// otherwise belong to an unconfigured logging system.
import { registerTenancyResolver } from '@/lib/log-context.ts';
import '@/lib/logging.ts';

import { getCustomerId, getLocationKey } from '@/utils/customer-utils.ts';
import { isSalesEnvironment } from '@/utils/envUtils.ts';

import config from './amplifyconfiguration.json';
import { App } from './app.tsx';
import './index.css';

const logger = getLogger(['main']);

// Stamped onto every record from here on. Pushed in rather than imported by `logging.ts` - see
// `log-context.ts` for why.
registerTenancyResolver(() => ({
    customer_id: getCustomerId(),
    location_key: getLocationKey(),
    is_test: isSalesEnvironment(),
}));

Amplify.configure({
    ...config,
    // @ts-expect-error configuration type
    oauth: {
        ...config.oauth,
        redirectSignIn: `${globalThis.location.origin}/`,
        redirectSignOut: `${globalThis.location.origin}/`,
    },
});

const container = document.querySelector('#root');
const root = createRoot(container!, {
    // Callback called when an error is thrown and not caught by an ErrorBoundary.
    onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
        logger.warn('Uncaught error', { error: error, componentStack: errorInfo.componentStack });
    }),
    // Callback called when React catches an error in an ErrorBoundary.
    onCaughtError: Sentry.reactErrorHandler(),
    // Callback called when React automatically recovers from errors.
    onRecoverableError: Sentry.reactErrorHandler(),
});

root.render(
    <StrictMode>
        <NuqsAdapter>
            <SWRConfig
                value={{
                    compare: dequal, // TODO: check performance impact
                }}
            >
                <App />
            </SWRConfig>
        </NuqsAdapter>
    </StrictMode>
);
